import Head from "next/head";
import { useRouter } from "next/router";
import { StructuredData } from "@/components/structured-data";
import { toAbsoluteMediaUrl, toAbsoluteUrl } from "@/lib/seo-url";
import {
  getStorefrontCanonicalOrigin,
  getStorefrontSiteConfig,
  type StorefrontSiteCode,
} from "@/lib/storefront-site";
import type { SeoData } from "@/lib/storefront-types";

type PageSeoHeadProps = {
  siteCode: StorefrontSiteCode;
  seo: SeoData | null;
  ogType?: string;
  structuredData?: object | object[];
};

/**
 * Single source of truth for a storefront page's title, description, canonical,
 * robots, Open Graph and Twitter Card tags, plus optional Schema.org JSON-LD.
 * Guarantees an absolute canonical / og:url even when the CMS supplies a
 * relative URL or none at all.
 */
export function PageSeoHead({
  siteCode,
  seo,
  ogType = "website",
  structuredData,
}: PageSeoHeadProps) {
  const router = useRouter();
  const origin = getStorefrontCanonicalOrigin(siteCode);
  const { brandName, defaultSeoDescription } = getStorefrontSiteConfig(siteCode);

  const title = seo?.seoTitle || seo?.pageTitle || brandName;
  const description = seo?.seoDescription || defaultSeoDescription;
  const imageUrl = toAbsoluteMediaUrl(seo?.seoImage?.url, origin) || null;
  const robots = `${seo?.noIndex ? "noindex" : "index"},${seo?.noFollow ? "nofollow" : "follow"}`;

  const rawPath = (router.asPath || "/").split(/[?#]/)[0] || "/";
  const requestPath =
    rawPath.length > 1 && rawPath.endsWith("/")
      ? rawPath.slice(0, -1)
      : rawPath;
  const canonicalUrl = toAbsoluteUrl(seo?.canonicalUrl || requestPath, origin);

  return (
    <>
      <Head>
        <title>{title}</title>
        {description ? (
          <meta name="description" content={description} />
        ) : null}
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content={robots} />
        <meta property="og:title" content={title} />
        <meta property="og:type" content={ogType} />
        <meta property="og:site_name" content={brandName} />
        <meta property="og:locale" content="en_GB" />
        {description ? (
          <meta property="og:description" content={description} />
        ) : null}
        <meta property="og:url" content={canonicalUrl} />
        {imageUrl ? <meta property="og:image" content={imageUrl} /> : null}
        <meta
          name="twitter:card"
          content={imageUrl ? "summary_large_image" : "summary"}
        />
        <meta name="twitter:title" content={title} />
        {description ? (
          <meta name="twitter:description" content={description} />
        ) : null}
        {imageUrl ? <meta name="twitter:image" content={imageUrl} /> : null}
      </Head>
      {structuredData ? <StructuredData data={structuredData} /> : null}
    </>
  );
}
